import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { storageScopeSentence } from '@/platform';
import { IconButton } from '@/components/primitives/icon-button';
import { Badge, Eyebrow } from '@/components/primitives/badge';
import { SlothAssetSlot } from '@/components/character/sloth-asset-slot';
import { useAssistantState } from './use-assistant-state';

interface AssistantPanelProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function AssistantPanel({ collapsed, onToggle }: AssistantPanelProps) {
  // 提示完全由現有資料推導，不使用 AI 生成（spec 8.2）。
  const { state, tip, reminderCount } = useAssistantState();

  if (collapsed) {
    return (
      <aside
        aria-label="樹懶助理"
        className="flex h-full w-[var(--spacing-assistant-collapsed)] shrink-0 flex-col items-center gap-3 py-4"
      >
        <IconButton
          label="展開助理區"
          tooltipSide="left"
          onClick={onToggle}
          icon={<CaretLeft size={18} />}
        />
        <div className="w-8">
          <SlothAssetSlot state={state} size="sm" decorative />
        </div>
        {reminderCount > 0 && (
          <span className="tabular text-[0.6875rem] font-semibold text-accent">
            {reminderCount}
          </span>
        )}
      </aside>
    );
  }

  return (
    <aside
      aria-label="樹懶助理"
      className="flex h-full w-[var(--spacing-assistant)] shrink-0 flex-col gap-4 py-4 pr-4 pl-2"
    >
      {/* Double bezel：外殼提供暖色底與髮絲邊，內核自帶高光。 */}
      <div className="bezel-shell flex min-h-0 flex-1 flex-col rounded-[var(--radius-shell)] p-1.5">
        <div className="bezel-core flex min-h-0 flex-1 flex-col gap-4 rounded-[calc(var(--radius-shell)-0.375rem)] p-4">
          <div className="flex items-center justify-between">
            <Eyebrow>助理</Eyebrow>
            <IconButton
              label="收合助理區"
              tooltipSide="left"
              onClick={onToggle}
              icon={<CaretRight size={18} />}
            />
          </div>

          <div className="mx-auto w-36">
            <SlothAssetSlot state={state} />
          </div>

          <div className="space-y-2 text-center">
            <p className="text-[0.9375rem] leading-relaxed text-text">{tip}</p>
            {reminderCount > 0 && <Badge tone="accent">{reminderCount} 件待提醒</Badge>}
          </div>

          <div className="mt-auto rounded-[var(--radius-control-lg)] bg-surface-sunken p-3">
            <p className="text-[0.8125rem] leading-relaxed text-text-muted">
              {storageScopeSentence()}記得定期到備份頁匯出一份。
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
}
